import * as React from 'react';
import { Autocomplete, Dialog, DialogContent, DialogTitle, IconButton, TextField, TextFieldVariants } from '@mui/material';
import { renderHelpText } from './renderHelpText';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { areIdentical } from '../../../shared/utils';
import HelpIcon from '@material-ui/icons/Help';
import { useState } from 'react';

export interface AutocompleteTextFieldProps {
    id?: string,
    inputId?: string,
    label?: React.ReactNode,
    helpText?: string,
    description?: string,
    invalid?: boolean,
    style?: React.CSSProperties,
    inputStyle?: React.CSSProperties,
    variant?: TextFieldVariants | 'nobox',
    multiSelect?: boolean,
    options: string[],
    onAddNewOption?: (newOption: string) => void;
    addNewLabel?: string;
    disabled?: boolean, // readOnly and greyed out
    readOnly?: boolean,
    disableClearable?: boolean,
    className?: string,
    placeholder?: string,
    dropup?: boolean,
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>,
    noOptionsText?: string,
    onBlur?: () => void,
    commitOnBlur?: boolean,
    autoComplete?: string,

    // Anchored item configuration - for special items that should stick to the top
    anchoredItem?: string, // The option value that should be anchored
    highlightedOptions?: string[], // Options that should appear visually highlighted
    groupBy?: (option: string) => string,
    groupLabelResolver?: (group: string) => React.ReactNode,
    renderItemIcon?: (option: string) => React.ReactNode, // Custom icon renderer for any option
    startAdornment?: React.ReactNode, // Element shown on the left side of the input
    optionLabelResolver?: (option: string) => string,
    searchTextResolver?: (option: string) => string,
    searchMatcher?: (inputValue: string, option: string) => boolean,
    isOptionDisabled?: (option: string) => boolean,

    onTextChanged?: (value: string) => void,

    // use if multiSelect=false
    value?: string,
    onValueChanged?: (values: string) => void,

    // use if multiSelect=true
    values?: string[],
    onValuesChanged?: (values: string[]) => void,
}

export const AutocompleteTextField: React.FC<AutocompleteTextFieldProps> = (props) => {
    const {
        id,
        inputId,
        label,
        helpText,
        description,
        invalid,
        style,
        inputStyle,

        onTextChanged,

        value,
        onValueChanged,

        values = [],
        onValuesChanged,

        options = [],
        multiSelect,
        onAddNewOption,
        addNewLabel,
        disabled,
        disableClearable,
        readOnly,
        className,
        placeholder,
        dropup,
        onKeyDown,
        variant = "outlined",
        onBlur,
        commitOnBlur,
        anchoredItem,
        highlightedOptions,
        groupBy,
        groupLabelResolver,
        renderItemIcon,
        startAdornment,
        optionLabelResolver,
        searchTextResolver,
        searchMatcher,
        isOptionDisabled,
        noOptionsText: noOptionsTextProp,
        autoComplete,
    } = props;

    const t = useTranslations();
    const resolveOptionLabel = React.useCallback((option: string) => optionLabelResolver?.(option) ?? option, [optionLabelResolver]);
    const [showHelp, setShowHelp] = useState(false);
    const [inputValue, setInputValue] = useState(value ? resolveOptionLabel(value) : '');

    React.useEffect(() => {
        if (!multiSelect) {
            setInputValue(value ? resolveOptionLabel(value) : '');
        }
    }, [value, multiSelect, resolveOptionLabel]);

    // Combine the value handling for single and multi select
    const selectedValue = multiSelect ? values : (value || null);

    const handleChange = React.useCallback((_event: any, newValue: string | string[] | null) => {
        if (disabled) return;

        if (multiSelect) {
            const valuesArray = ((newValue as string[]) || []).map(v => v.startsWith(addNewLabel ?? t(TK.create))
                ? v.substring((v as string).indexOf('"') + 1, (v as string).lastIndexOf('"'))
                : v);

            // Check for new options (freeSolo creates new entries)
            if (onAddNewOption) {
                valuesArray.forEach(v => {
                    if (!options.includes(v)) {
                        onAddNewOption(v);
                    }
                });
            }

            onValuesChanged && onValuesChanged(valuesArray);
        } else {
            const singleValue = (newValue as string ?? '').startsWith(addNewLabel ?? t(TK.create))
                ? (newValue as string).substring((newValue as string).indexOf('"') + 1, (newValue as string).lastIndexOf('"'))
                : (newValue as string);

            setInputValue(singleValue ? resolveOptionLabel(singleValue) : '');

            // Check if it's a new option
            if (onAddNewOption && singleValue && !options.includes(singleValue)) {
                onAddNewOption(singleValue);
            }

            onValueChanged && onValueChanged(singleValue);
        }
    }, [disabled, multiSelect, onAddNewOption, onValueChanged, onValuesChanged, options, resolveOptionLabel, t]);

    const handleInputChange = React.useCallback((_event: any, newInputValue: string, reason: string) => {
        setInputValue(newInputValue);
        if (!disabled && onTextChanged && (reason === 'input' || reason === 'clear')) {
            onTextChanged(newInputValue);
        }
    }, [disabled, onTextChanged]);

    const filterOptions = React.useCallback((options: string[], state: any) => {
        // Martelo por causa do bug no Autocomplete do mui onde não renderiza as options devolvidas nesta
        // função, devolvendo um [] no meio da escrita força ele a funcionar, vai-se lá saber porquê.
        if (options.length > 100 && state.inputValue.length === 2 && !(searchMatcher && /^\d+$/.test(state.inputValue))) return [];

        const filtered = options.filter(option =>
            searchMatcher?.(state.inputValue, option) ?? areIdentical(state.inputValue, searchTextResolver?.(option) ?? option)
        );

        // If freeSolo is enabled and input doesn't match any option, show the option to add it
        if (onAddNewOption && state.inputValue !== '' && !filtered.some(option => resolveOptionLabel(option) === state.inputValue)) {
            filtered.push(`${addNewLabel ?? t(TK.create)} "${state.inputValue}"`);
        }

        return filtered;
    }, [onAddNewOption, resolveOptionLabel, searchMatcher, searchTextResolver, t]);

    const handleBlur = React.useCallback(() => {
        if (commitOnBlur && !multiSelect) {
            onValueChanged?.(inputValue ?? '');
        }
        onBlur?.();
    }, [commitOnBlur, inputValue, multiSelect, onBlur, onValueChanged]);

    // Custom render option to support anchored items and custom icons
    const renderOption = React.useCallback((props: any, option: string) => {
        const isAnchored = anchoredItem === option;
        const isHighlighted = isAnchored || (highlightedOptions?.includes(option) ?? false);
        const isDisabled = isOptionDisabled?.(option) ?? false;
        const icon = renderItemIcon?.(option);
        const label = resolveOptionLabel(option);

        return (
            <li
                {...props}
                key={option}
                style={{
                    ...props.style,
                    display: 'flex',
                    alignItems: 'center',
                    gap: icon ? 'var(--spacer-XS)' : undefined,
                    opacity: isDisabled ? 0.55 : 1,
                    ...(isHighlighted && {
                        backgroundColor: 'var(--surface-brand-soft)',
                        borderLeft: '3px solid var(--brand-secondary)',
                        fontWeight: 600,
                    }),
                    ...(isAnchored && {
                        borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
                        position: 'sticky',
                        top: '-8px',
                        zIndex: 1,
                    })
                }}
            >
                {icon}
                {label}
            </li>
        );
    }, [anchoredItem, highlightedOptions, isOptionDisabled, renderItemIcon, resolveOptionLabel]);

    return (
        <div id={id} className={className} style={style}>
            <Autocomplete
                id={id ? `${id}-input` : undefined}
                multiple={multiSelect}
                freeSolo={!!onAddNewOption}
                options={options}
                getOptionDisabled={isOptionDisabled}
                groupBy={groupBy}
                disableClearable={disableClearable}
                value={selectedValue as any}
                inputValue={multiSelect ? undefined : inputValue}
                onChange={handleChange}
                popupIcon={null} // remove arrow button
                getOptionLabel={o => resolveOptionLabel(o)}
                onInputChange={handleInputChange}
                onBlur={handleBlur}
                disabled={disabled}
                readOnly={readOnly}
                filterOptions={filterOptions}
                size="small"
                disablePortal={dropup}
                noOptionsText={noOptionsTextProp ?? t(TK.notFoundInSet)}
                renderOption={renderOption}
                renderGroup={groupBy ? (params) => (
                    <li key={params.key}>
                        <div style={{
                            padding: 'var(--spacer-XS) var(--spacer-S)',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            letterSpacing: '0.03em',
                            textTransform: 'uppercase',
                            color: 'var(--text-muted)',
                            backgroundColor: 'var(--bg-light)',
                            borderBottom: 'var(--border-thin) solid var(--surface-border-subtle)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}>
                            {groupLabelResolver?.(params.group) ?? params.group}
                        </div>
                        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                    </li>
                ) : undefined}
                slotProps={{
                    chip: {
                        size: "small" as const
                    },
                    ...(groupBy && {
                        listbox: {
                            sx: { padding: 0 },
                        },
                    }),
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputProps={{
                            ...params.inputProps,
                            id: inputId ?? params.inputProps.id,
                            autoComplete: autoComplete ?? params.inputProps.autoComplete,
                        }}
                        label={helpText
                            ? <div>{label} <IconButton size="small" onClick={() => setShowHelp(true)}><HelpIcon/></IconButton></div>
                            : label}
                        placeholder={placeholder}
                        helperText={description}
                        error={invalid}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: startAdornment ? (
                                <>{startAdornment}{params.InputProps.startAdornment}</>
                            ) : params.InputProps.startAdornment,
                        }}
                        sx={{
                            "& .MuiInputBase-input": {
                                ...inputStyle,
                            },
                            ...variant === 'nobox' && {
                                "& .MuiInput-underline:before": { borderBottom: "none" },
                                // "& .MuiInput-underline:after": { borderBottom: "none" },
                                // "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" }
                            },
                        }}
                        variant={variant === 'nobox' ? 'standard' : variant}
                        onKeyDown={onKeyDown}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                    />
                )}
            />

            {(helpText?.length &&
                <Dialog open={showHelp} onClose={() => setShowHelp(false)}>
                    <DialogTitle>{label}</DialogTitle>
                    <DialogContent>{renderHelpText(helpText)}</DialogContent>
                </Dialog>
            ) || undefined}
        </div>
    );
}
